/**
 * Site-admin audience flip modal.
 *
 * Two directions:
 *
 *   personal -> shared: pick optional managers, submit. The server runs V4
 *     (reuses V2 visibility) on each manager id; failures surface via the
 *     parsed error banner.
 *
 *   shared -> personal: V5 will 422 if any managers are attached. The UI
 *     pre-flights this by showing the current manager count and requiring
 *     an explicit "Yes, remove and flip" confirm. We send
 *     `manager_contact_ids: []` to satisfy the server contract; the API
 *     then unhooks managers atomically with the flip.
 */
import { useState } from "react";
import {
  IonButton,
  IonContent,
  IonHeader,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { displayMsg } from "pymble-ui/components";
import {
  useFlipPresetAudience,
  useUpdatePreset,
} from "../../hooks/usePresets";
import { ContactPicker } from "./ContactPicker";
import { parsePresetError } from "./presetErrorParser";
import type { Preset, PresetAudience } from "../../types/preset";

import "./presets.css";

interface FlipAudienceModalProps {
  preset: Preset;
  target: PresetAudience;
  onDismiss: () => void;
  onFlipped: () => void;
}

export function FlipAudienceModal({
  preset,
  target,
  onDismiss,
  onFlipped,
}: FlipAudienceModalProps) {
  const flip = useFlipPresetAudience(preset.id);
  const update = useUpdatePreset(preset.id);

  const isPersonalToShared = target === "shared";
  const [managerIds, setManagerIds] = useState<number[]>(
    isPersonalToShared ? [] : []
  );
  const [errorTitle, setErrorTitle] = useState<string | null>(null);
  const [errorBullets, setErrorBullets] = useState<string[]>([]);

  const currentManagerCount = preset.manager_contact_ids.length;
  const needsConfirm = !isPersonalToShared && currentManagerCount > 0;
  const [confirmed, setConfirmed] = useState(!needsConfirm);

  const handleSubmit = async () => {
    setErrorTitle(null);
    setErrorBullets([]);
    try {
      // shared -> personal: the API hard-blocks the flip while managers are
      // still attached (V5, manager_contact_ids_must_be_empty). Clear them
      // first via PUT, then flip. Two calls (the API has no atomic
      // clear-and-flip) - if the PUT succeeds but the flip fails the preset
      // is left shared with no managers, which the operator can retry.
      if (!isPersonalToShared && currentManagerCount > 0) {
        await update.mutateAsync({ manager_contact_ids: [] });
      }
      await flip.mutateAsync({
        target_audience: target,
        // For shared -> personal we MUST send an empty list (V5; managers
        // were cleared above). For personal -> shared we send whatever the
        // operator picked (possibly empty - allowed, the preset just has no
        // managers).
        manager_contact_ids: isPersonalToShared ? managerIds : [],
      });
      displayMsg(
        isPersonalToShared
          ? "Preset flipped to shared"
          : "Preset flipped to personal",
        { type: "success", position: "top", duration: 1500 }
      );
      onFlipped();
    } catch (err) {
      const parsed = parsePresetError(err);
      const title = parsed?.title ?? "Failed to flip audience - please try again";
      setErrorTitle(title);
      setErrorBullets(parsed?.bullets ?? []);
      // Also surface via a toast. The shared->personal path is two calls
      // (clear managers, then flip); if the clear succeeds but the flip
      // fails the inline error would be lost should the modal be dismissed,
      // leaving the preset shared-with-no-managers silently. The toast
      // survives unmount so the operator always sees the failure and can
      // retry the flip (managers are already cleared). Codex Phase 2 review.
      displayMsg(title, {
        type: "danger",
        position: "top",
        duration: 4000,
      });
    }
  };

  return (
    <>
      <IonHeader>
        <IonToolbar>
          <IonTitle>
            Flip to {isPersonalToShared ? "shared" : "personal"}
          </IonTitle>
          <IonButton
            slot="end"
            fill="clear"
            onClick={onDismiss}
            disabled={flip.isPending || update.isPending}
          >
            Cancel
          </IonButton>
        </IonToolbar>
      </IonHeader>
      <IonContent className="preset-modal-content">
        {errorTitle && (
          <div className="preset-modal-error" role="alert">
            <strong>{errorTitle}</strong>
            {errorBullets.length > 0 && (
              <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                {errorBullets.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {isPersonalToShared ? (
          <>
            <p>
              Flipping to <strong>shared</strong> makes this preset visible
              to anyone with the right group permissions, and lets you
              attach managers who can edit it (but not change the manager
              list).
            </p>
            <div className="preset-modal-field">
              <span className="preset-modal-field-label">
                Managers (optional)
              </span>
              <span className="preset-modal-field-hint">
                Can also be added later from the preset's detail page.
              </span>
              <ContactPicker
                mode="numeric"
                selectedIds={managerIds}
                onChange={setManagerIds}
                placeholder="Search staff..."
                types={["staff"]}
                unknownLabel="Manager"
              />
            </div>
          </>
        ) : (
          <>
            <p>
              Flipping to <strong>personal</strong> hides this preset from
              everyone except the owner.
            </p>
            {needsConfirm && (
              <div className="preset-modal-error" role="alert">
                <strong>
                  {currentManagerCount} manager{currentManagerCount === 1
                    ? ""
                    : "s"}{" "}
                  will be removed
                </strong>
                <p style={{ margin: "6px 0 10px" }}>
                  Personal presets cannot have managers. They will be
                  removed first and then the preset flipped - this cannot be
                  undone without re-adding them by hand.
                </p>
                <IonButton
                  fill="outline"
                  color="danger"
                  size="small"
                  onClick={() => setConfirmed(true)}
                  disabled={confirmed}
                >
                  {confirmed
                    ? "Confirmed"
                    : "Yes, remove managers and flip"}
                </IonButton>
              </div>
            )}
          </>
        )}

        <div className="preset-modal-actions">
          <IonButton
            fill="outline"
            onClick={onDismiss}
            disabled={flip.isPending || update.isPending}
          >
            Cancel
          </IonButton>
          <IonButton
            color={isPersonalToShared ? "secondary" : "danger"}
            onClick={handleSubmit}
            disabled={flip.isPending || update.isPending || !confirmed}
          >
            {flip.isPending || update.isPending
              ? "Flipping..."
              : isPersonalToShared
                ? "Flip to shared"
                : "Flip to personal"}
          </IonButton>
        </div>
      </IonContent>
    </>
  );
}
